import { Icon } from '../../../../../components/ui/Icon';
import {
  getCourseAttendanceStatus,
  getCoursesForRoom,
  getCourseStatus,
  getFeaturedCourse,
  hasOverdueUnattendedCourse
} from '../app/scheduleModel';
import type { Course, Room } from '../app/types';

interface RoomSelectorProps {
  rooms: Room[];
  courses: Course[];
  now: Date;
  selectedRoomId: string;
  onSelect: (roomId: string) => void;
}

function getFeaturedStatus(course: Course, now: Date): { className: string; label: string } {
  const attendanceStatus = getCourseAttendanceStatus(course, now);
  const timeLabel = getCourseStatus(course.startTime, course.endTime, now) === 'current'
    ? '上课中'
    : '待上课';

  if (attendanceStatus === 'attended') {
    return { className: 'attended', label: `${timeLabel}·已点名` };
  }

  return {
    className: timeLabel === '上课中' ? 'current-unattended' : 'future-unattended',
    label: `${timeLabel}·未点名`
  };
}

export function RoomSelector({
  rooms,
  courses,
  now,
  selectedRoomId,
  onSelect
}: RoomSelectorProps) {
  return (
    <aside className="room-selector" aria-label="教室选择">
      <div className="panel-title-row">
        <h2>教室 <small>{rooms.length}间</small></h2>
      </div>
      <div className="room-list room-list--grid">
        {rooms.map((room) => {
          const roomCourses = getCoursesForRoom(courses, room.id);
          const featuredCourse = getFeaturedCourse(roomCourses, now);
          const hasOverdueCourse = hasOverdueUnattendedCourse(roomCourses, now);
          const isIdle = !featuredCourse && !hasOverdueCourse;
          const featuredStatus = featuredCourse ? getFeaturedStatus(featuredCourse, now) : null;

          return (
            <button
              key={room.id}
              type="button"
              className={`room-card room-card--${isIdle ? 'idle' : 'active'} ${selectedRoomId === room.id ? 'is-selected' : ''}`}
              aria-label={room.name}
              aria-pressed={selectedRoomId === room.id}
              onClick={() => onSelect(room.id)}
            >
              <span className="room-card__content">
                <strong>{room.name}</strong>
                {featuredCourse && featuredStatus ? (
                  <span className="room-card__course">
                    <span className="room-card__title">{featuredCourse.name}</span>
                    <span className="room-card__meta">
                      <span className="room-card__time">
                        <Icon name="time-line" size="sm" />
                        <span>{featuredCourse.startTime.slice(11, 16)}~{featuredCourse.endTime.slice(11, 16)}</span>
                      </span>
                      <span className="room-card__teacher">
                        <Icon name="user-6-line" size="sm" />
                        <span>{featuredCourse.teacherName}</span>
                      </span>
                      <span className={`room-card__course-status room-card__course-status--${featuredStatus.className}`}>
                        <i aria-hidden="true" />
                        <span>{featuredStatus.label}</span>
                      </span>
                    </span>
                  </span>
                ) : (
                  <span className="room-card__title">
                    {roomCourses.length > 0 ? '今日课程已结束' : '今日无课'}
                  </span>
                )}
              </span>
              {hasOverdueCourse ? (
                <em className="room-card__alert">有超时未点名课程</em>
              ) : featuredCourse ? (
                <span className="room-card__alert-placeholder" aria-hidden="true" />
              ) : (
                <em className="room-card__idle-status">空闲中</em>
              )}
            </button>
          );
        })}
      </div>
    </aside>
  );
}
